import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Project, Settings } from '../../shared/types';
import type { WeekStart } from './period';
import { api } from './api';

interface Store {
  projects: Project[];
  activeProjects: Project[];
  settings: Settings;
  weekStartsOn: WeekStart;
  reloadProjects: () => Promise<void>;
  reloadSettings: () => Promise<void>;
  signOut: () => Promise<void>;
}

const StoreContext = createContext<Store | null>(null);

const FALLBACK_SETTINGS: Settings = { default_rate_inr: 0, week_starts_on: 1, owner_name: null };

/** Projects and settings are read on almost every screen, so they load once here. */
export function StoreProvider({ children, onSignOut }: { children: ReactNode; onSignOut: () => void }) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [settings, setSettings] = useState<Settings>(FALLBACK_SETTINGS);
  const [ready, setReady] = useState(false);

  const reloadProjects = useCallback(async () => setProjects(await api.listProjects()), []);
  const reloadSettings = useCallback(async () => setSettings(await api.settings()), []);

  useEffect(() => {
    void Promise.all([reloadProjects(), reloadSettings()]).finally(() => setReady(true));
  }, [reloadProjects, reloadSettings]);

  const signOut = useCallback(async () => {
    await api.logout();
    onSignOut();
  }, [onSignOut]);

  const value = useMemo<Store>(
    () => ({
      projects,
      activeProjects: projects.filter((p) => !p.archived),
      settings,
      weekStartsOn: (settings.week_starts_on === 0 ? 0 : 1) as WeekStart,
      reloadProjects,
      reloadSettings,
      signOut,
    }),
    [projects, settings, reloadProjects, reloadSettings, signOut],
  );

  if (!ready) return null;
  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useStore(): Store {
  const store = useContext(StoreContext);
  if (!store) throw new Error('useStore must be used inside a StoreProvider');
  return store;
}

/** Effective hourly rate for a project, after the account-default fallback. */
export function effectiveRate(project: Project | undefined, settings: Settings): number {
  return project?.rate_inr ?? settings.default_rate_inr;
}
